import axios from 'axios';
import { config } from '../config/environment';

// Create axios instance
export const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if the 401 came from the login request (wrong credentials)
      const isLoginRequest = error.config?.url === 'login' || error.config?.url?.endsWith('/login');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (data: { email: string; password: string }) => api.post('/login', data),
  register: (data: any) => api.post('/register', data),
  logout: () => api.post('/logout'),
  user: () => api.get('/user'),
  getActiveTimer: () => api.get('/user/active-timer'),
};

export const searchAPI = {
  global: (q: string, limit = 10) => api.get('/search', { params: { q, limit } }),
};

export const firmAPI = {
  getAll: (params?: any) => api.get('/firms', { params }),
  getById: (id: number) => api.get(`/firms/${id}`),
  create: (data: any) => api.post('/firms', data),
  update: (id: number, data: any) => api.put(`/firms/${id}`, data),
  delete: (id: number) => api.delete(`/firms/${id}`),
  register: (data: any) => api.post('/firm-registration', data),
  approve: (id: number, data?: any) => api.post(`/firms/${id}/approve`, data),
  reject: (id: number, data?: any) => api.post(`/firms/${id}/reject`, data),
  // Members
  members: (firmId: number) => api.get(`/firms/${firmId}/members`),
  addExistingMember: (firmId: number, userId: number) => api.post(`/firms/${firmId}/members/add-existing`, { user_id: userId }),
  createMember: (firmId: number, data: any) => api.post(`/firms/${firmId}/members`, data),
  toggleFinance: (firmId: number, userId: number) => api.post(`/firms/${firmId}/members/${userId}/toggle-finance`),
  toggleActive: (firmId: number, userId: number) => api.post(`/firms/${firmId}/members/${userId}/toggle-active`),
  resetPassword: (firmId: number, userId: number) => api.post(`/firms/${firmId}/members/${userId}/reset-password`),
  removeMember: (firmId: number, userId: number) => api.delete(`/firms/${firmId}/members/${userId}`),
  // Settings
  getSettings: (firmId: number) => api.get(`/firms/${firmId}/settings`),
  updateSettings: (firmId: number, data: any) => api.put(`/firms/${firmId}/settings`, data),
  // Wallet
  getWalletBalance: (firmId: number) => api.get(`/firms/${firmId}/wallet/balance`),
  addDeposit: (firmId: number, data: any) => api.post(`/firms/${firmId}/wallet/deposit`, data),
  getWalletTransactions: (firmId: number, params?: any) => api.get(`/firms/${firmId}/wallet/transactions`, { params }),
  getStripePublishableKey: () => api.get('/stripe/publishable-key'),
  createStripePaymentIntent: (firmId: number, data: any) => api.post(`/firms/${firmId}/wallet/stripe-payment-intent`, data),
  confirmStripePayment: (firmId: number, data: any) => api.post(`/firms/${firmId}/wallet/confirm-stripe-payment`, data),
  // Auto-pay
  getOrCreateStripeCustomer: (firmId: number) => api.get(`/firms/${firmId}/autopay/customer`),
  getPaymentMethods: (firmId: number) => api.get(`/firms/${firmId}/autopay/payment-methods`),
  createSetupIntent: (firmId: number) => api.post(`/firms/${firmId}/autopay/setup-intent`),
  attachPaymentMethod: (firmId: number, data: any) => api.post(`/firms/${firmId}/autopay/payment-methods`, data),
  setDefaultPaymentMethod: (firmId: number, paymentMethodId: number) => api.put(`/firms/${firmId}/autopay/payment-methods/${paymentMethodId}/default`),
  removePaymentMethod: (firmId: number, paymentMethodId: number) => api.delete(`/firms/${firmId}/autopay/payment-methods/${paymentMethodId}`),
  getAutoPaySettings: (firmId: number) => api.get(`/firms/${firmId}/autopay/settings`),
  updateAutoPaySettings: (firmId: number, data: any) => api.put(`/firms/${firmId}/autopay/settings`, data),
};

export const projectAPI = {
  getAll: (params?: any) => api.get('/projects', { params }),
  getById: (id: number) => api.get(`/projects/${id}`),
  create: (data: any) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return api.post('/projects', data, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
  },
  update: (id: number, data: any) => api.put(`/projects/${id}`, data),
  delete: (id: number) => api.delete(`/projects/${id}`),
  getSettings: (id: number) => api.get(`/projects/${id}/settings`),
  updateSettings: (id: number, data: any) => api.put(`/projects/${id}/settings`, data),
  // Project Billable Activities
  getActivities: (projectId: number) => api.get(`/projects/${projectId}/activities`),
  updateActivity: (projectId: number, data: any) => api.put(`/projects/${projectId}/activities`, data),
};

export const caseAPI = {
  getAll: (params?: any) => api.get('/cases', { params }),
  getById: (id: number, params?: any) => api.get(`/cases/${id}`, { params }),
  getMeta: () => api.get('/cases/meta'),
  create: (data: any, onProgress?: (progress: number) => void) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    let config: any = undefined;
    
    if (isFormData) {
      config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      if (onProgress) {
        config.onUploadProgress = (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        };
      }
    }
    
    return api.post('/cases', data, config);
  },
  update: (id: number, data: any) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return api.put(`/cases/${id}`, data, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
  },
  delete: (id: number) => api.delete(`/cases/${id}`),
  assignVendor: (id: number, payload: { vendor_id: number; vendor_user_id?: number; due_date?: string }) => api.post(`/cases/${id}/assign-vendor`, payload),
  reassignVendor: (id: number, payload: { vendor_id: number; vendor_user_id?: number; due_date?: string; reassignment_reason: string }) => api.post(`/cases/${id}/reassign-vendor`, payload),
  getVendorAssignmentHistory: (id: number) => api.get(`/cases/${id}/vendor-assignment-history`),
  getFieldRevisions: (id: number, params?: { per_page?: number; page?: number }) =>
    api.get(`/cases/${id}/field-revisions`, { params }),
  // Timers
  listTimeEntries: (id: number) => api.get(`/cases/${id}/timer`),
  getActiveTimers: (id: number) => api.get(`/cases/${id}/timer/active`),
  startTimer: (id: number, payload?: { title?: string; notes?: string }) => api.post(`/cases/${id}/timer/start`, payload),
  resumeTimer: (id: number) => api.post(`/cases/${id}/timer/resume`),
  stopTimer: (id: number, payload?: { deduction_seconds?: number }) => api.post(`/cases/${id}/timer/stop`, payload),
  finalizeTimer: (id: number, payload: any) => api.post(`/cases/${id}/timer/finalize`, payload),
  resetTimer: (id: number, payload?: any) => api.post(`/cases/${id}/timer/reset`, payload),
  updateTimeEntry: (caseId: number, entryId: number, payload: any) => api.put(`/cases/${caseId}/timer/${entryId}`, payload),
  deleteTimeEntry: (caseId: number, entryId: number) => api.delete(`/cases/${caseId}/timer/${entryId}`),
  uploadFile: (id: number, file: File, additionalData?: FormData, onProgress?: (progress: number) => void) => {
    const formData = additionalData || new FormData();
    if (!additionalData) {
      formData.append('file', file);
    }
    const config: any = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    if (onProgress) {
      config.onUploadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    return api.post(`/cases/${id}/files`, formData, config);
  },
  uploadFilesBatch: (id: number, files: File[], additionalData?: FormData, onProgress?: (progress: number) => void) => {
    const formData = additionalData || new FormData();
    files.forEach((file) => formData.append('files[]', file));
    const config: any = {
      headers: { 'Content-Type': 'multipart/form-data' },
    };
    if (onProgress) {
      config.onUploadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    return api.post(`/cases/${id}/files/batch`, formData, config);
  },
  uploadZip: (id: number, file: File, additionalData?: FormData, onProgress?: (progress: number) => void) => {
    const formData = additionalData || new FormData();
    if (!additionalData) {
      formData.append('file', file);
    }
    const config: any = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    if (onProgress) {
      config.onUploadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    return api.post(`/cases/${id}/files/zip`, formData, config);
  },
  deleteFile: (caseId: number, fileId: number) => api.delete(`/cases/${caseId}/files/${fileId}`),
  addComment: (id: number, data: any) => api.post(`/cases/${id}/comments`, data),
  updateComment: (caseId: number, commentId: number, data: any) => api.put(`/cases/${caseId}/comments/${commentId}`, data),
  deleteComment: (caseId: number, commentId: number) => api.delete(`/cases/${caseId}/comments/${commentId}`),
  
  // New comment functionality
  updateCaseAndAddComment: (caseId: number, data: any) => api.post(`/cases/${caseId}/update-and-comment`, data),
  getComments: (caseId: number) => api.get(`/cases/${caseId}/comments`),
  getCommentNotifyUsers: (caseId: number) => api.get(`/cases/${caseId}/comment-notify-users`),
  reopen: (caseId: number, options?: { skip_vendor_reopen?: boolean }) => api.post(`/cases/${caseId}/reopen`, options || {}),
  listFiles: (id: number) => api.get(`/cases/${id}/files`),
  downloadFile: (caseId: number, fileId: number, onProgress?: (progress: number) => void) => {
    const config: any = { responseType: 'blob' };
    if (onProgress) {
      config.onDownloadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    return api.get(`/cases/${caseId}/files/${fileId}/download`, config);
  },
  downloadFilesZip: (id: number, fileIds: number[], zipName?: string, onProgress?: (progress: number) => void) => {
    const config: any = { responseType: 'blob' };
    if (onProgress) {
      config.onDownloadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    return api.post(`/cases/${id}/files/download-zip`, { file_ids: fileIds, zip_name: zipName || 'case_files' }, config);
  },
  syncFilesToClient: (id: number) => api.post(`/cases/${id}/sync-files-to-client`),
  importFromSharePoint: (id: number, data: any) => api.post(`/cases/${id}/files/sharepoint`, data),
  getMicrosoftAuthUrl: () => api.get('/microsoft/auth-url'),
  getMicrosoftStatus: () => api.get('/microsoft/status'),
  disconnectMicrosoft: () => api.post('/microsoft/disconnect'),
  // Document review (bookmarks & redactions)
  getDocumentReview: (caseId: number, fileId: number) =>
    api.get(`/cases/${caseId}/files/${fileId}/document-review`),
  importDocumentReviewOutlines: (caseId: number, fileId: number) =>
    api.post(`/cases/${caseId}/files/${fileId}/document-review/import-outlines`),
  generateDocumentReviewPdf: (caseId: number, fileId: number) =>
    api.get(`/cases/${caseId}/files/${fileId}/document-review/generate-pdf`, { responseType: 'blob' }),
  createBookmark: (caseId: number, fileId: number, data: { page_number: number; title: string; sort_order?: number; x?: number; y?: number; width?: number; height?: number }) =>
    api.post(`/cases/${caseId}/files/${fileId}/bookmarks`, data),
  updateBookmark: (caseId: number, fileId: number, bookmarkId: number, data: { page_number?: number; title?: string; sort_order?: number; x?: number | null; y?: number | null; width?: number | null; height?: number | null }) =>
    api.put(`/cases/${caseId}/files/${fileId}/bookmarks/${bookmarkId}`, data),
  deleteBookmark: (caseId: number, fileId: number, bookmarkId: number) =>
    api.delete(`/cases/${caseId}/files/${fileId}/bookmarks/${bookmarkId}`),
  createRedaction: (caseId: number, fileId: number, data: { page_number: number; x: number; y: number; width: number; height: number }) =>
    api.post(`/cases/${caseId}/files/${fileId}/redactions`, data),
  updateRedaction: (caseId: number, fileId: number, redactionId: number, data: { page_number?: number; x?: number; y?: number; width?: number; height?: number }) =>
    api.put(`/cases/${caseId}/files/${fileId}/redactions/${redactionId}`, data),
  deleteRedaction: (caseId: number, fileId: number, redactionId: number) =>
    api.delete(`/cases/${caseId}/files/${fileId}/redactions/${redactionId}`),
};

export const adminAgreementAPI = {
  list: () => api.get('/admin/agreements'),
  get: (id: number) => api.get(`/admin/agreements/${id}`),
  create: (data: any) => api.post('/admin/agreements', data),
  update: (id: number, data: any) => api.put(`/admin/agreements/${id}`, data),
  delete: (id: number) => api.delete(`/admin/agreements/${id}`),
  toggleForceResign: (id: number, force_resign?: boolean) => api.post(`/admin/agreements/${id}/toggle-force-resign`, { force_resign }),
};

export const firmCustomAgreementAPI = {
  create: (data: FormData | any) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return api.post('/firm-custom-agreements', data, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
  },
  update: (id: number, data: FormData | any) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return api.put(`/firm-custom-agreements/${id}`, data, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
  },
  get: (id: number) => api.get(`/firm-custom-agreements/${id}`),
  delete: (id: number) => api.delete(`/firm-custom-agreements/${id}`),
  cloneDefault: () => api.get('/firm-custom-agreements/clone-default'),
  downloadPdf: (id: number) => api.get(`/firm-custom-agreements/${id}/download`, { responseType: 'blob' }),
};

export const userAPI = {
  list: (params?: { q?: string; status?: string; roles?: string[]; firm_id?: number }) =>
    api.get('/users', { params }),
};

export const supportAPI = {
  submitTicket: (data: FormData) =>
    api.post('/support/ticket', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const userActivityAPI = {
  getAll: (params?: any) => api.get('/user-activities', { params }),
  getStats: (params?: any) => api.get('/user-activities/stats', { params }),
  getUsers: () => api.get('/user-activities/users'),
  getById: (id: number) => api.get(`/user-activities/${id}`),
  logScreenView: (screen: string, metadata?: any) => api.post('/user-activities/log-screen', { screen, metadata }),
  /** Log inactivity nudge: action = 'shown' | 'continue' | 'pause_timer', metadata optional e.g. { case_id, firm_id } */
  logInactivityNudge: (action: 'shown' | 'continue' | 'pause_timer', metadata?: { case_id?: number; firm_id?: number }) =>
    api.post('/user-activities/log-inactivity-nudge', { action, metadata }),
};

// Timer configuration (global activities & dispositions) - PMP/Site Admin only
export const timerConfigAPI = {
  // Activities (timer activity types)
  getActivities: () => api.get('/activities'),
  createActivity: (data: { name: string; sort_order?: number; is_active?: boolean }) => api.post('/activities', data),
  updateActivity: (id: number, data: { name?: string; sort_order?: number; is_active?: boolean }) => api.put(`/activities/${id}`, data),
  deleteActivity: (id: number) => api.delete(`/activities/${id}`),
  // Dispositions (timer disposition types)
  getDispositions: () => api.get('/dispositions'),
  createDisposition: (data: { name: string; is_active?: boolean }) => api.post('/dispositions', data),
  updateDisposition: (id: number, data: { name?: string; is_active?: boolean }) => api.put(`/dispositions/${id}`, data),
  deleteDisposition: (id: number) => api.delete(`/dispositions/${id}`),
};